import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  Sparkles,
  MessageCircle,
  ShoppingBag,
  Activity,
  Building2,
  Users,
  Briefcase,
  DollarSign,
  Wallet,
  User,
  Settings,
  HelpCircle,
  LogOut,
  MessageSquarePlus
} from 'lucide-react';
import { useUser, AccountType } from '@/contexts/UserContext';
import { supabase } from '@/integrations/supabase/client';
import sliceLogo from '@/assets/slice-logo-v2.png';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { FeedbackModal } from '@/components/feedback/FeedbackModal';

const getMainNavItems = (accountType: AccountType) => {
  switch (accountType) {
    case 'talent':
    case 'agent':
      return [
        { name: 'SliceAI', href: '/slice-ai', icon: Sparkles },
        { name: 'Signal', href: '/feed', icon: MessageCircle },
        { name: 'Marketplace', href: '/marketplace', icon: ShoppingBag },
        { name: 'Deal Progress', href: '/deal-progress', icon: Activity },
      ];
    case 'brand':
      return [
        { name: 'SliceAI', href: '/slice-ai', icon: Sparkles },
        { name: 'Signal', href: '/feed', icon: MessageCircle },
        { name: 'Deal Management', href: '/brand-dashboard', icon: Building2 },
        { name: 'Talent', href: '/talent-marketplace', icon: Users },
      ];
    case 'venture':
      return [
        { name: 'SliceAI', href: '/slice-ai', icon: Sparkles },
        { name: 'Venture Dashboard', href: '/venture-dashboard', icon: Briefcase },
        { name: 'Deal Dashboard', href: '/brand-dashboard', icon: Building2 },
        { name: 'Signal', href: '/feed', icon: MessageCircle },
        { name: 'Talent', href: '/talent-marketplace', icon: Users },
      ];
    default:
      return [
        { name: 'Settings', href: '/settings', icon: Settings },
      ];
  }
};

const getBottomNavItems = (accountType: AccountType) => {
  const items = [
    { name: 'Wallet', href: '/wallet', icon: Wallet },
  ];
  
  if (accountType === 'talent' || accountType === 'agent') {
    items.push({ name: 'Holdings', href: '/holdings', icon: DollarSign });
  }
  
  items.push(
    { name: 'Profile', href: '/profile', icon: User },
    { name: 'Settings', href: '/settings', icon: Settings },
    { name: 'Help', href: '/help', icon: HelpCircle }
  );
  
  return items;
};

interface NavItemProps {
  item: { name: string; href: string; icon: React.ElementType };
  isActive: boolean;
}

const NavItem: React.FC<NavItemProps> = ({ item, isActive }) => {
  const Icon = item.icon;
  
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={item.href}
            className={cn(
              "sidebar-nav-item",
              isActive && "sidebar-nav-item-active"
            )}
          >
            <Icon className="h-5 w-5" />
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right" className="bg-foreground text-background">
          {item.name}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const SidebarNavigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, accountType } = useUser();
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);

  const mainNavItems = getMainNavItems(accountType);
  const bottomNavItems = getBottomNavItems(accountType);

  const handleLogout = async () => {
    // Clear local state first so redirect works even if signOut hangs
    logout();
    // Clear any Supabase tokens from localStorage
    Object.keys(localStorage).filter(k => k.startsWith('sb-')).forEach(k => localStorage.removeItem(k));
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Full page reload to clear in-memory auth state
    window.location.href = '/auth';
  };

  const isActive = (href: string) => {
    if (href === '/wallet') {
      return location.pathname.startsWith('/wallet') || location.pathname === '/transaction-history';
    }
    if (href === '/settings') {
      return location.pathname.startsWith('/settings');
    }
    return location.pathname === href;
  };

  return (
    <>
      <aside className="sidebar-nav fixed left-0 top-0 bottom-0 w-16 flex flex-col items-center py-4 z-50">
        {/* Logo */}
        <div className="mb-8">
          <img 
            src={sliceLogo} 
            alt="Slice" 
            className="h-8 w-8"
          />
        </div>

        {/* Main Navigation */}
        <nav className="flex-1 flex flex-col items-center gap-2">
          {mainNavItems.map((item) => (
            <NavItem
              key={item.name}
              item={item}
              isActive={isActive(item.href)}
            />
          ))}
        </nav>

        {/* Bottom Navigation */}
        <div className="flex flex-col items-center gap-2 mt-auto">
          {bottomNavItems.map((item) => (
            <NavItem
              key={item.name}
              item={item}
              isActive={isActive(item.href)}
            />
          ))}

          {/* Feedback Button */}
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setFeedbackOpen(true)}
                  className="sidebar-nav-item"
                >
                  <MessageSquarePlus className="h-5 w-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-foreground text-background">
                Send Feedback
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Logout Button */}
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleLogout}
                  className="sidebar-nav-item text-destructive/70 hover:text-destructive"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-foreground text-background">
                Sign Out
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </aside>

      <FeedbackModal isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </>
  );
};
